import { motion } from "framer-motion";
import { TrendingUp, ShieldHalf, Landmark, Activity } from "lucide-react";

const models = [
  {
    n: "01",
    icon: TrendingUp,
    title: "Equity Income Systems",
    tag: "Equities",
    body: "Dividend-focused and quality-growth equity portfolios engineered to compound and throw off predictable, rising cashflow over time.",
    metrics: ["Dividend yield", "Compounding SIPs", "Quality factor"],
  },
  {
    n: "02",
    icon: ShieldHalf,
    title: "Options & Hedging Overlays",
    tag: "Hedging",
    body: "Covered calls, protective puts and defined-risk spreads that generate yield on holdings while capping downside during volatility.",
    metrics: ["Covered calls", "Protective puts", "Credit spreads"],
  },
  {
    n: "03",
    icon: Landmark,
    title: "Fixed-Income Instruments",
    tag: "Income",
    body: "Bonds, debt funds and structured notes laddered for steady coupons — the ballast that pays the bills through every cycle.",
    metrics: ["Bond ladders", "Debt funds", "Coupon income"],
  },
  {
    n: "04",
    icon: Activity,
    title: "Derivatives & Arbitrage",
    tag: "Market-neutral",
    body: "Hedged, market-neutral strategies that harvest spreads and mispricings with strict risk limits — return uncorrelated to the index.",
    metrics: ["Calendar spreads", "Cash-future arb", "Delta-neutral"],
  },
];

export default function CashflowModels() {
  return (
    <section id="models" data-testid="cashflow-models" className="relative z-10 py-24 md:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-primary mb-5">/ The Cashflow Models</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              Four engines,<br /><span className="text-amber">one kingdom</span>
            </h2>
            <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">
              Every durable portfolio is a system of cashflow engines. We combine equities, hedging
              strategies and income instruments into models tuned to your risk and horizon.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
          {models.map((m, i) => (
            <motion.div
              key={m.n}
              data-testid={`model-card-${m.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 2) * 0.08, duration: 0.6 }}
              className="group bg-background p-8 sm:p-10 hover:bg-card transition-colors"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
                  <m.icon size={20} className="text-primary" />
                </div>
                <span className="font-display font-black text-5xl text-border group-hover:text-primary/30 transition-colors leading-none">{m.n}</span>
              </div>
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber">{m.tag}</span>
              <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mt-2 mb-4 group-hover:text-primary transition-colors">
                {m.title}
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed mb-7 max-w-md">{m.body}</p>
              <div className="flex flex-wrap gap-2">
                {m.metrics.map((x) => (
                  <span key={x} className="font-mono-accent text-[10px] uppercase tracking-[0.12em] border border-border px-2.5 py-1 text-muted-foreground">
                    {x}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
